import { makeOfflineChecksum } from "@/helpers/drm";
import { toRaw } from "vue";
import { getDebugMode } from "@/helpers/helperFunctions";

import type { AuthType, ReadingSession } from "../../wps/session";
import { deserializeSession } from "../../wps/helpers";

import ReaderServiceWorker, { SWMessageEvent } from "./ServiceWorker";

import { PublicationsDB } from "../../../modules/service-worker/publicationsDb";
import { IdentifiersRepository } from "../../../modules/service-worker/identifiersRepo";

export type WorkerCommand =
  | "init"
  | "identifier"
  | "store"
  | "check"
  | "claim"
  | "_hello"
  | "_save"
  | "_save-force";

export interface WorkerMessage {
  id: string;
  key: WorkerCommand;
  value: unknown;
}

export interface OfflineMetadata {
  init: {
    device: string;
    token: string;
    type: AuthType;
  };
  session: unknown;
  finalized: boolean;
  checksum: string;
}

export type OfflineDownloadProgress = (
  numerator: number,
  denominator: number
) => void;

export class IncompleteDownloadError extends Error {
  constructor(message) {
    super(message);
    this.name = "IncompleteDownloadError";
  }
}

export class OutOfSpaceError extends Error {
  constructor(message) {
    super(message);
    this.name = "OutOfSpaceError";
  }
}

export class PublicationExpiredError extends Error {
  constructor(message) {
    super(message);
    this.name = "PublicationExpiredError";
  }
}

export class PublicationCorruptError extends Error {
  constructor(message) {
    super(message);
    this.name = "PublicationCorruptError";
  }
}

interface ExpiringPath {
  path: string;
  expiry: number;
  checksum: string;
}

class OfflineReading {
  public readonly worker: ReaderServiceWorker;
  private token2path: Record<string, ExpiringPath> = {};
  private path2identifier: [string, string] = ["empty", "empty"];

  public readonly init: Promise<void>;

  private identifiers = new IdentifiersRepository(new PublicationsDB());

  constructor() {
    this.worker = new ReaderServiceWorker();
    this.worker.addEventListener("message", (event) =>
      this.handleMessage(event)
    );
    this.init = this.initialize();
  }

  public get supported() {
    return this.worker.supported;
  }

  private async sendMessage(
    key: WorkerCommand,
    value: unknown,
    sendTo: ServiceWorker | null = null
  ): Promise<WorkerMessage> {
    return await this.worker.sendMessage(key, value, sendTo);
  }

  private async initialize(sendTo: ServiceWorker | null = null) {
    if (!this.supported) return; // SW not supported

    let offlineData = localStorage.getItem("DM__offlineData");
    if (!offlineData) {
      offlineData = "{}";
      localStorage.setItem("DM__offlineData", offlineData);
    }
    let offlineMap = localStorage.getItem("DM__offlineMap");
    if (!offlineMap) {
      offlineMap = "{}";
      localStorage.setItem("DM__offlineMap", offlineMap);
    } else {
      this.token2path = JSON.parse(offlineMap);
      let needsUpdate = false;
      for (const [token, epath] of Object.entries(this.token2path)) {
        // Make sure the token is still valid
        if (epath.expiry < Date.now() / 1000) {
          delete this.token2path[token];
          needsUpdate = true;
        }

        // Check checksum for tampering
        const checksum = await makeOfflineChecksum(
          `${epath.path}|${epath.expiry}`
        );
        if (epath.checksum !== checksum) {
          delete this.token2path[token];
          needsUpdate = true;
        }
      }
      if (needsUpdate)
        localStorage.setItem("DM__offlineMap", JSON.stringify(this.token2path));
    }
    // console.log("init", offlineData);

    await this.sendMessage("init", JSON.parse(offlineData), sendTo);
  }

  private handleMessage(event: SWMessageEvent<WorkerMessage>) {
    const debugModeEnabled = getDebugMode();

    const data = event.data as WorkerMessage;
    if (!data.key?.startsWith("_")) return;
    switch (data.key.substring(1)) {
      case "hello":
        if (debugModeEnabled) console.log("SW said hello");
        this.initialize(
          event.source instanceof ServiceWorker ? event.source : null
        );
        if (this.path2identifier[0] !== "empty") {
          this.current(this.path2identifier[0], this.path2identifier[1]);
        } else console.warn("No current path set");
        break;
      case "save-force":
        localStorage.setItem("DM__offlineData", JSON.stringify(data.value));
        this.initialize(
          event.source instanceof ServiceWorker ? event.source : null
        );
        break;
      case "save":
        localStorage.setItem("DM__offlineData", JSON.stringify(data.value));
        break;
      default:
        console.warn("Unknown SW message key", data.key);
        break;
    }
  }

  lookup(token: string): string | undefined {
    const epath = this.token2path[token];
    if (!epath) return;
    if (epath.expiry < Date.now() / 1000) {
      delete this.token2path[token];
      localStorage.setItem("DM__offlineMap", JSON.stringify(this.token2path));
      return;
    }
    return epath.path;
  }

  async store(data: OfflineMetadata) {
    if (!this.worker.supported) return; // SW not supported

    const expiry =
      (data.session as ReadingSession).refresh.ttl > 0
        ? Math.floor(Date.now() / 1000) +
          (data.session as ReadingSession).refresh.ttl
        : Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 30;

    this.token2path[data.init.token] = {
      path: (data.session as ReadingSession).path,
      expiry,
      checksum: await makeOfflineChecksum(
        `${(data.session as ReadingSession).path}|${expiry}`
      ),
    };
    localStorage.setItem("DM__offlineMap", JSON.stringify(this.token2path));

    data.session = toRaw(data.session);
    return await this.sendMessage("store", toRaw(data));
  }

  async check(key: string): Promise<ReadingSession | null> {
    if (!this.worker.supported) return null; // SW not supported
    const sessionData = (await this.sendMessage("check", key)).value;
    return deserializeSession(sessionData);
  }

  async current(path: string, identifier: string): Promise<void> {
    if (!this.worker.supported) return; // SW not supported
    this.path2identifier = [path, identifier];
    await this.identifiers.set(path, identifier);
    await this.sendMessage("identifier", {
      path,
      identifier,
    });
  }
}

export default new OfflineReading();
